import { useEffect, useRef, useState } from "react";

export default function useWebRTCStream(wsUrl) {
  const [status, setStatus] = useState("Initializing video stream...");
  const [stream, setStream] = useState(null);

  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryDelayRef = useRef(1000);
  const disconnectTimerRef = useRef(null);

  useEffect(() => {
    if (!wsUrl) {
      setStatus("Missing WebSocket URL");
      return;
    }

    let isCancelled = false;

    const clearRetryTimer = () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const clearDisconnectTimer = () => {
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
    };

    const cleanupConnections = () => {
      clearDisconnectTimer();
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };

    const scheduleRetry = () => {
      if (isCancelled) return;
      if (retryTimerRef.current) return;
      const delay = retryDelayRef.current;
      retryDelayRef.current = Math.min(retryDelayRef.current * 2, 10000);
      setStatus(`Retrying in ${Math.round(delay / 1000)}s...`);
      retryTimerRef.current = setTimeout(() => {
        if (!isCancelled) startConnection();
      }, delay);
    };

    const startConnection = () => {
      cleanupConnections();
      setStream(null);
      setStatus("Connecting...");

      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "turn:69.48.203.221:3478", username: "admin", credential: "12345" }
        ]
      });
      pcRef.current = pc;

      pc.addTransceiver("video", { direction: "recvonly" });

      pc.ontrack = (event) => {
        if (isCancelled) return;
        setStream(event.streams[0]);
        setStatus("Live");
        retryDelayRef.current = 1000;
      };

      pc.onconnectionstatechange = () => {
        if (isCancelled) return;
        if (pc.connectionState === "connected") {
          retryDelayRef.current = 1000;
          clearDisconnectTimer();
          return;
        }
        if (pc.connectionState === "failed") {
          setStatus("Disconnected");
          scheduleRetry();
          return;
        }
        if (pc.connectionState === "disconnected") {
          setStatus("Disconnected");
          if (!disconnectTimerRef.current) {
            disconnectTimerRef.current = setTimeout(() => {
              if (pc.connectionState === "disconnected") {
                scheduleRetry();
              }
            }, 5000);
          }
        }
      };

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        if (isCancelled) return;
        setStatus("Negotiating...");

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        ws.send(JSON.stringify({ type: "rtc_offer", sdp: offer.sdp }));
      };

      ws.onmessage = async (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === "rtc_answer") {
          if (pc.signalingState !== "have-local-offer") {
            return;
          }
          const remoteDesc = new RTCSessionDescription({
            type: "answer",
            sdp: msg.sdp
          });
          await pc.setRemoteDescription(remoteDesc);
          setStatus("Connected");
        }
      };

      ws.onerror = () => {
        if (!isCancelled) {
          setStatus("Connection Error");
          scheduleRetry();
        }
      };

      ws.onclose = () => {
        if (!isCancelled) {
          setStatus("Disconnected");
          scheduleRetry();
        }
      };
    };
    startConnection();

    return () => {
      isCancelled = true;
      clearRetryTimer();
      clearDisconnectTimer();
      cleanupConnections();
    };
  }, [wsUrl]);

  return { stream, status };
}
